import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { expensesAPI, categoriesAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { getCurrentMonthRange } from '../shared/utils/date';
import { API_CONFIG } from '../shared/constants/api';
import { useTranslation } from '../i18n';
import './HouseDetail.css';

const API_URL = API_CONFIG.BASE_URL;

function HouseDetail() {
  const { houseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useTranslation();

  const [house, setHouse] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [members, setMembers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [balances, setBalances] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('expenses');

  // Harcama modalı
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [confirmData, setConfirmData] = useState({ title: '', message: '', onConfirm: null, type: 'warning' });
  const [successMessage, setSuccessMessage] = useState('');
  const [expenseFormData, setExpenseFormData] = useState({
    amount: '',
    payer_id: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    category: '',
    is_shared: true, // Varsayılan olarak ortak harcama
    participants: []
  });

  // Filtreler - Varsayılan olarak bulunduğumuz ayın ilk ve son günü
  const [filters, setFilters] = useState(() => {
    const monthRange = getCurrentMonthRange();
    return {
      start_date: monthRange.start_date,
      end_date: monthRange.end_date,
      category: '',
      payer_id: '',
      is_shared: ''
    };
  });

  const getDateLocale = () => {
    const localeMap = { tr: 'tr-TR', en: 'en-US', fr: 'fr-FR' };
    return localeMap[language] || 'tr-TR';
  };

  useEffect(() => {
    const initData = async () => {
      await loadHouseData();
      loadCategories();
    };
    initData();
    // eslint-disable-next-line
  }, [houseId]);

  useEffect(() => {
    if (house) {
      loadExpenses();
      loadBalances(); // Filtre değiştiğinde bakiyeleri de güncelle
    }
    // eslint-disable-next-line
  }, [filters, house]);

  const loadCategories = async () => {
    try {
      const data = await categoriesAPI.getCategories();
      // Backend direkt array döndürüyor
      setCategories(Array.isArray(data) ? data : (data.data || []));
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error);
      setCategories(['Market', 'Faturalar', 'Temizlik', 'Eğlence', 'Diğer']);
    }
  };

  const loadHouseData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');

      // House detaylarını yükle
      const houseResponse = await fetch(`${API_URL}/houses/${houseId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const houseData = await houseResponse.json();
      console.log('House data:', houseData);
      setHouse(houseData);

      // Üyeleri yükle - mobil ile aynı şekilde
      const membersResponse = await fetch(`${API_URL}/houses/${houseId}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (membersResponse.ok) {
        const membersData = await membersResponse.json();
        console.log('Members data:', membersData);
        setMembers(membersData);
      }

      // Bakiyeleri hesapla
      await loadBalances();
    } catch (error) {
      console.error('Ev verileri yüklenemedi:', error);
      if (error.response?.status === 404 || error.response?.status === 403) {
        alert(t('house.noAccess'));
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  // Mobil ile aynı - basit üye yükleme
  const loadMembers = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const membersResponse = await fetch(`${API_URL}/houses/${houseId}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (membersResponse.ok) {
        const membersData = await membersResponse.json();
        console.log('loadMembers - Members data:', membersData);
        setMembers(membersData);
      }
    } catch (error) {
      console.error('Üyeler yüklenemedi:', error);
    }
  };

  const loadExpenses = async () => {
    try {
      const params = {};
      Object.keys(filters).forEach(key => {
        if (filters[key] !== '') {
          params[key] = filters[key];
        }
      });

      const data = await expensesAPI.getExpenses(houseId, params);
      console.log('Loaded expenses:', data);
      setExpenses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Harcamalar yüklenemedi:', error);
      setExpenses([]);
    }
  };

  const loadBalances = async () => {
    try {
      // Filtreleri backend'e gönder
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const data = await expensesAPI.calculateBalances(houseId, params);
      setBalances(data);
    } catch (error) {
      console.error('Bakiyeler hesaplanamadı:', error);
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    const monthRange = getCurrentMonthRange();
    setFilters({
      start_date: monthRange.start_date,
      end_date: monthRange.end_date,
      category: '',
      payer_id: '',
      is_shared: ''
    });
  };

  const handleExpenseFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setExpenseFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Bireysel harcama seçildiğinde, her zaman kendini seç
    if (name === 'is_shared' && !checked) {
      setExpenseFormData(prev => ({
        ...prev,
        participants: [],
        payer_id: user?.id || ''
      }));
    }
  };

  const handleParticipantToggle = (userId) => {
    setExpenseFormData(prev => {
      const participants = prev.participants.includes(userId)
        ? prev.participants.filter(id => id !== userId)
        : [...prev.participants, userId];
      return { ...prev, participants };
    });
  };

  const handleAddExpense = () => {
    setEditingExpense(null);
    const isMultiUserHouse = members.length > 1;
    setExpenseFormData({
      amount: '',
      payer_id: user?.id || '',
      date: new Date().toISOString().split('T')[0],
      description: '',
      category: categories[0] || '',
      is_shared: isMultiUserHouse, // Birden fazla üye varsa ortak harcama ile başla
      participants: isMultiUserHouse ? members.map(m => m.id) : [] // Tüm üyeleri seç
    });
    setShowExpenseModal(true);
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseFormData({
      amount: expense.amount,
      payer_id: expense.payer_id,
      date: expense.date.split('T')[0],
      description: expense.description || '',
      category: expense.category,
      is_shared: expense.is_shared,
      participants: expense.participants || []
    });
    setShowExpenseModal(true);
  };

  const handleSaveExpense = async (e) => {
    e.preventDefault();

    try {
      // Eğer ortak harcama işaretli ama katılımcı seçilmemişse uyar
      if (expenseFormData.is_shared && expenseFormData.participants.length === 0) {
        alert(t('expenses.sharedRequired'));
        return;
      }

      const expenseData = {
        ...expenseFormData,
        amount: parseFloat(expenseFormData.amount),
        payer_id: parseInt(expenseFormData.payer_id),
        // Eğer ortak harcama değilse veya katılımcı yoksa, boş array gönder
        participants: expenseFormData.is_shared && expenseFormData.participants.length > 0
          ? expenseFormData.participants.map(id => parseInt(id))
          : []
      };

      if (editingExpense) {
        await expensesAPI.updateExpense(houseId, editingExpense.id.toString(), expenseData);
      } else {
        await expensesAPI.createExpense(houseId, expenseData);
      }

      setShowExpenseModal(false);
      loadExpenses();
      loadBalances();
    } catch (error) {
      alert(`${t('expenses.saveError')}: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    setConfirmData({
      title: t('expenses.deleteConfirm.title'),
      message: `${t('expenses.deleteConfirm.message')}\n\n⚠️ ${t('common.irreversible')}`,
      type: 'danger',
      onConfirm: () => confirmDeleteExpense(expenseId)
    });
    setShowConfirmModal(true);
  };

  const confirmDeleteExpense = async (expenseId) => {
    setShowConfirmModal(false);
    try {
      await expensesAPI.deleteExpense(houseId, expenseId.toString());
      loadExpenses();
      loadBalances();
      setSuccessMessage(t('expenses.deleteConfirm.success'));
      setShowSuccessModal(true);
    } catch (error) {
      setConfirmData({
        title: `❌ ${t('common.error')}`,
        message: error.response?.data?.error || error.message || t('expenses.deleteConfirm.error'),
        type: 'error',
        onConfirm: () => setShowConfirmModal(false),
        singleButton: true
      });
      setShowConfirmModal(true);
    }
  };

  const handleUpdateMemberRole = async (memberId, newRole) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${API_URL}/houses/${houseId}/members/${memberId}/role`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ role: newRole })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('members.roleUpdateError'));
      }

      // Üyeleri yeniden yükle
      loadMembers();
      setSuccessMessage(t('members.roleUpdated'));
      setShowSuccessModal(true);
    } catch (error) {
      setConfirmData({
        title: `❌ ${t('common.error')}`,
        message: error.message || t('members.roleUpdateError'),
        type: 'error',
        onConfirm: () => setShowConfirmModal(false),
        singleButton: true
      });
      setShowConfirmModal(true);
      loadMembers(); // Hata durumunda da listeyi yenile
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    setConfirmData({
      title: t('members.removeConfirm.title'),
      message: `"${memberName}" ${t('members.removeConfirm.message')}\n\n⚠️ ${t('common.irreversible')}`,
      type: 'warning',
      onConfirm: () => confirmRemoveMember(memberId, memberName)
    });
    setShowConfirmModal(true);
  };

  const confirmRemoveMember = async (memberId, memberName) => {
    setShowConfirmModal(false);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${API_URL}/houses/${houseId}/members/${memberId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('members.removeConfirm.error'));
      }

      await loadMembers();
      setSuccessMessage(t('members.removeConfirm.success', { name: memberName }));
      setShowSuccessModal(true);
    } catch (error) {
      setConfirmData({
        title: `❌ ${t('common.error')}`,
        message: error.message || t('members.removeConfirm.error'),
        type: 'error',
        onConfirm: () => setShowConfirmModal(false),
        singleButton: true
      });
      setShowConfirmModal(true);
    }
  };

  const getTotalAmount = () => {
    return expenses.reduce((sum, expense) => sum + expense.amount, 0).toFixed(2);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(getDateLocale(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  if (!house) {
    return <div className="error">{t('house.notFound')}</div>;
  }

  // Mevcut kullanıcının rolünü members listesinden bul
  const currentMember = members.find(m => m.id === user?.id);
  const userRole = currentMember?.role || 'member';
  const isAdmin = userRole === 'admin';
  const canEdit = userRole === 'admin' || userRole === 'moderator';

  // Tek kullanıcı mı çoklu kullanıcı mı?
  const isSingleUser = members.length === 1;
  const isMultiUser = members.length > 1;

  return (
    <div className="house-detail">
      <header className="house-header">
        <button onClick={handleBack} className="btn-back">← {t('house.back')}</button>
        <div className="house-info">
          <h1>{house.name}</h1>
          <div className="house-meta">
            {isMultiUser && (
              <span className="invite-code">{t('house.inviteCode')}: <strong>{house.invite_code}</strong></span>
            )}
            {isSingleUser && (
              <span className="single-user-badge">👤 {t('house.singleUser')}</span>
            )}
            {isMultiUser && (
              <span className={`role-badge role-${userRole}`}>
                {userRole === 'admin' ? t('dashboard.roles.admin') :
                 userRole === 'moderator' ? t('dashboard.roles.moderator') : t('dashboard.roles.member')}
              </span>
            )}
          </div>
        </div>
      </header>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'expenses' ? 'active' : ''}`}
          onClick={() => setActiveTab('expenses')}
        >
          📋 {t('house.tabs.expenses')}
        </button>
        <button
          className={`tab ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('summary')}
        >
          💰 {t('house.tabs.summary')}
        </button>
        <button
          className={`tab ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveTab('members')}
        >
          👥 {t('house.tabs.members')} ({members.length})
        </button>
      </div>

      <div className="tab-content">
        {/* HARCAMALAR SEKMESİ */}
        {activeTab === 'expenses' && (
          <div className="expenses-tab">
            <div className="expenses-header">
              <h2>{t('expenses.title')}</h2>
              <button onClick={handleAddExpense} className="btn btn-add-expense">
                <span className="btn-icon">➕</span>
                <span className="btn-text">{t('expenses.addNew')}</span>
              </button>
            </div>

            {/* Filtreler */}
            <div className="filters">
              {/* Hızlı Filtreler */}
              <div className="quick-filters">
                <button
                  className={`quick-filter-btn ${filters.is_shared === '' && filters.payer_id === '' ? 'active' : ''}`}
                  onClick={() => setFilters({...filters, is_shared: '', payer_id: ''})}
                >
                  📊 {t('filters.all')}
                </button>
                <button
                  className={`quick-filter-btn ${filters.is_shared === 'true' ? 'active' : ''}`}
                  onClick={() => setFilters({...filters, is_shared: 'true', payer_id: ''})}
                >
                  👥 {t('filters.sharedExpenses')}
                </button>
                <button
                  className={`quick-filter-btn ${filters.payer_id === String(user?.id) && filters.is_shared === 'false' ? 'active' : ''}`}
                  onClick={() => setFilters({...filters, is_shared: 'false', payer_id: String(user?.id)})}
                >
                  🙋 {t('filters.myPersonal')}
                </button>
              </div>

              {/* Detaylı Filtreler */}
              <div className="detailed-filters">
              <div className="form-group">
                <label>{t('filters.startDate')}</label>
                <input
                  type="date"
                  name="start_date"
                  value={filters.start_date}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="form-group">
                <label>{t('filters.endDate')}</label>
                <input
                  type="date"
                  name="end_date"
                  value={filters.end_date}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="form-group">
                <label>{t('filters.category')}</label>
                <select name="category" value={filters.category} onChange={handleFilterChange}>
                  <option value="">{t('filters.all')}</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('filters.payer')}</label>
                <select name="payer_id" value={filters.payer_id} onChange={handleFilterChange}>
                  <option value="">{t('filters.all')}</option>
                  {members.map(member => (
                    <option key={member.id} value={member.id}>{member.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button onClick={resetFilters} className="btn btn-secondary">
                  🔄 {t('filters.reset')}
                </button>
              </div>
              </div>
            </div>

            {/* Özet Bilgi */}
            {expenses.length > 0 && (
              <div className="expenses-summary">
                <div>
                  <strong>{expenses.length}</strong> {t('expenses.found')}
                </div>
                <div>
                  {t('common.total')}: <strong className="total-amount">₺{getTotalAmount()}</strong>
                </div>
              </div>
            )}

            {/* Harcama Listesi */}
            {expenses.length === 0 ? (
              <div className="empty-state">
                <h3>{t('expenses.empty.title')}</h3>
                <p>{t('expenses.empty.description')}</p>
              </div>
            ) : (
              <div className="expenses-list">
                {expenses.map((expense) => (
                  <div key={expense.id} className="expense-card">
                    <div className="expense-header">
                      <div>
                        <h3>{expense.category}</h3>
                        {expense.description && (
                          <p className="expense-description">{expense.description}</p>
                        )}
                        <p className="expense-amount">₺{expense.amount.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="expense-details">
                      <span>📅 {formatDate(expense.date)}</span>
                      <span className="expense-category">🏷️ {expense.category}</span>
                      <span>💳 {expense.payer_name}</span>
                      <span className={`badge ${expense.is_shared ? 'badge-shared' : 'badge-personal'}`}>
                        {expense.is_shared ? `👥 ${t('expenses.shared')}` : `👤 ${t('expenses.personal')}`}
                      </span>
                    </div>
                    <div className="expense-actions-bottom">
                      {/* Herkes kendi eklediği harcamayı düzenleyebilir, admin/moderator hepsini düzenleyebilir */}
                      {(canEdit || expense.payer_id === user?.id) && (
                        <button
                          onClick={() => handleEditExpense(expense)}
                          className="btn btn-primary btn-sm"
                        >
                          ✏️ {t('common.edit')}
                        </button>
                      )}
                    </div>
                    {expense.is_shared && expense.participant_names && (
                      <div className="expense-participants">
                        <strong>{t('expenses.participants')}:</strong> {expense.participant_names.join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* HESAPLAR/ÖZET SEKMESİ */}
        {activeTab === 'summary' && (
          <div className="summary-tab">
            {/* Sekme Başlığı */}
            {!isSingleUser && <h2>{t('summary.title')}</h2>}
            {isSingleUser && <h2>📊 {t('summary.personalTitle')}</h2>}

            {/* Filtreler - Hesaplar sekmesinde de göster */}
            <div className="filters">
              {/* Hızlı Filtreler */}
              <div className="quick-filters">
                <button
                  className={`quick-filter-btn ${filters.is_shared === '' && filters.payer_id === '' ? 'active' : ''}`}
                  onClick={() => setFilters({...filters, is_shared: '', payer_id: ''})}
                >
                  📊 {t('filters.all')}
                </button>
                <button
                  className={`quick-filter-btn ${filters.is_shared === 'true' ? 'active' : ''}`}
                  onClick={() => setFilters({...filters, is_shared: 'true', payer_id: ''})}
                >
                  👥 {t('filters.sharedExpenses')}
                </button>
                <button
                  className={`quick-filter-btn ${filters.payer_id === String(user?.id) && filters.is_shared === 'false' ? 'active' : ''}`}
                  onClick={() => setFilters({...filters, is_shared: 'false', payer_id: String(user?.id)})}
                >
                  🙋 {t('filters.myPersonal')}
                </button>
              </div>

              {/* Detaylı Filtreler */}
              <div className="detailed-filters">
                <div className="form-group">
                  <label>{t('filters.startDate')}</label>
                  <input
                    type="date"
                    name="start_date"
                    value={filters.start_date}
                    onChange={handleFilterChange}
                  />
                </div>

                <div className="form-group">
                  <label>{t('filters.endDate')}</label>
                  <input
                    type="date"
                    name="end_date"
                    value={filters.end_date}
                    onChange={handleFilterChange}
                  />
                </div>

                <div className="form-group">
                  <label>{t('filters.category')}</label>
                  <select name="category" value={filters.category} onChange={handleFilterChange}>
                    <option value="">{t('filters.all')}</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('filters.payer')}</label>
                  <select name="payer_id" value={filters.payer_id} onChange={handleFilterChange}>
                    <option value="">{t('filters.all')}</option>
                    {members.map(member => (
                      <option key={member.id} value={member.id}>{member.name}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button onClick={resetFilters} className="btn btn-secondary">
                    🔄 {t('filters.reset')}
                  </button>
                </div>
              </div>
            </div>

            {isSingleUser ? (
              <>

                {/* Özet Kartları - Mobil tarzı */}
                <div className="summary-cards-grid">
                  <div className="summary-stat-card total">
                    <div className="stat-label">{t('summary.total')}</div>
                    <div className="stat-amount">₺{expenses.reduce((sum, e) => sum + parseFloat(e.amount), 0).toFixed(2)}</div>
                    <div className="stat-count">{expenses.length} {t('expenses.expense')}</div>
                  </div>
                  <div className="summary-stat-card personal">
                    <div className="stat-label">👤 {t('summary.personal')}</div>
                    <div className="stat-amount">₺{expenses.filter(e => !e.is_shared).reduce((sum, e) => sum + parseFloat(e.amount), 0).toFixed(2)}</div>
                    <div className="stat-count">{expenses.filter(e => !e.is_shared).length} {t('expenses.expense')}</div>
                  </div>
                </div>

                {/* Bireysel Harcamalar Listesi */}
                {expenses.length > 0 && (
                  <div className="personal-expenses-section">
                    <h3>👤 {t('summary.personalExpenses')}</h3>
                    <div className="personal-expenses-grid">
                      {expenses.map((expense) => (
                        <div key={expense.id} className="personal-expense-card">
                          <div className="personal-expense-header">
                            <span className="expense-category-badge personal">{expense.category}</span>
                            <span className="expense-amount-large">₺{parseFloat(expense.amount).toFixed(2)}</span>
                          </div>
                          <div className="personal-expense-meta">
                            <span className="expense-date">📅 {new Date(expense.date).toLocaleDateString(getDateLocale())}</span>
                          </div>
                          {expense.description && (
                            <p className="expense-description-text">{expense.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {expenses.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-icon">📝</div>
                    <p>{t('summary.noExpenses')}</p>
                    <p className="empty-hint">{t('summary.addExpenseHint')}</p>
                  </div>
                )}
              </>
            ) : (
              <>

                {/* Harcama Özeti */}
                <div className="expense-summary-section">
                  <h3>📊 {t('summary.personalTitle')}</h3>
                  <div className="summary-cards-row">
                    <div className="summary-card total">
                      <div className="summary-card-label">{t('summary.total')}</div>
                      <div className="summary-card-amount">₺{expenses.reduce((sum, e) => sum + parseFloat(e.amount), 0).toFixed(2)}</div>
                      <div className="summary-card-count">{expenses.length} {t('expenses.expense')}</div>
                    </div>
                    <div className="summary-card shared">
                      <div className="summary-card-label">👥 {t('expenses.shared')}</div>
                      <div className="summary-card-amount">₺{expenses.filter(e => e.is_shared).reduce((sum, e) => sum + parseFloat(e.amount), 0).toFixed(2)}</div>
                      <div className="summary-card-count">{expenses.filter(e => e.is_shared).length} {t('expenses.expense')}</div>
                    </div>
                    <div className="summary-card personal">
                      <div className="summary-card-label">👤 {t('expenses.personal')}</div>
                      <div className="summary-card-amount">₺{expenses.filter(e => !e.is_shared).reduce((sum, e) => sum + parseFloat(e.amount), 0).toFixed(2)}</div>
                      <div className="summary-card-count">{expenses.filter(e => !e.is_shared).length} {t('expenses.expense')}</div>
                    </div>
                  </div>
                </div>

                {/* Bireysel Harcamalar */}
                {expenses.filter(e => !e.is_shared).length > 0 && (
                  <div className="personal-expenses-list">
                    <h3>👤 {t('summary.personalExpenses')}</h3>
                    {expenses.filter(e => !e.is_shared).map((expense) => {
                      const payer = members.find(m => m.id === expense.payer_id);
                      return (
                        <div key={expense.id} className="personal-expense-item">
                          <div className="personal-expense-main">
                            <div className="personal-expense-info">
                              <strong className="expense-category personal">{expense.category}</strong>
                              {expense.description && (
                                <span className="expense-description"> - {expense.description}</span>
                              )}
                              <div className="expense-meta">
                                <span className="expense-date">
                                  📅 {new Date(expense.date).toLocaleDateString(getDateLocale())}
                                </span>
                                <span className="expense-payer">
                                  👤 {payer?.name || t('common.unknown')}
                                </span>
                              </div>
                            </div>
                            <div className="personal-expense-amount">
                              ₺{parseFloat(expense.amount).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Ortak Harcama Listesi */}
                {expenses.filter(e => e.is_shared).length > 0 && (
                  <div className="shared-expenses-list">
                    <h3>👥 {t('summary.sharedExpenses')}</h3>
                    {expenses.filter(e => e.is_shared).map((expense) => {
                      const payer = members.find(m => m.id === expense.payer_id);
                      const participants = expense.participants || [];
                      return (
                        <div key={expense.id} className="shared-expense-item">
                          <div className="shared-expense-main">
                            <div className="shared-expense-info">
                              <strong className="expense-category">{expense.category}</strong>
                              {expense.description && (
                                <span className="expense-description"> - {expense.description}</span>
                              )}
                              <div className="expense-meta">
                                <span className="expense-date">
                                  📅 {new Date(expense.date).toLocaleDateString(getDateLocale())}
                                </span>
                                <span className="expense-payer">
                                  💳 {t('summary.paying')}: {payer?.name || t('common.unknown')}
                                </span>
                              </div>
                            </div>
                            <div className="shared-expense-amount">
                              ₺{parseFloat(expense.amount).toFixed(2)}
                            </div>
                          </div>
                          {participants.length > 0 && (
                            <div className="shared-expense-participants">
                              <span className="participants-label">{t('expenses.participants')}:</span>
                              <div className="participants-list">
                                {participants.map(userId => {
                                  const member = members.find(m => m.id === userId);
                                  return (
                                    <span key={userId} className="participant-badge">
                                      {member?.name || t('common.unknown')}
                                    </span>
                                  );
                                })}
                              </div>
                              <div className="per-person-amount">
                                {t('expenses.perPerson')}: ₺{(parseFloat(expense.amount) / participants.length).toFixed(2)}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Bakiye ve Hesaplaşma */}
                {balances && balances.balances && balances.balances.length > 0 ? (
              <>
                <div className="balances-section">
                  <h3>{t('summary.balances.title')}</h3>
                  <div className="balances-grid">
                    {balances.balances.map((balance) => {
                      const netAmount = parseFloat(balance.net) || 0;
                      return (
                        <div key={balance.user_id} className="balance-card">
                          <div className="balance-card-header">
                            <span className="member-name">{balance.user_name}</span>
                            <span className={`balance-status ${balance.status}`}>
                              {balance.status}
                            </span>
                          </div>
                          <div className="balance-details">
                            <div className="balance-item">
                              <span>{t('summary.balances.paid')}:</span>
                              <strong>₺{parseFloat(balance.paid).toFixed(2)}</strong>
                            </div>
                            <div className="balance-item">
                              <span>{t('summary.balances.expected')}:</span>
                              <strong>₺{parseFloat(balance.expected).toFixed(2)}</strong>
                            </div>
                            <div className="balance-item balance-net">
                              <span>{t('summary.balances.status')}:</span>
                              <strong className={netAmount >= 0 ? 'positive' : 'negative'}>
                                {netAmount >= 0 ? '+' : ''}{netAmount.toFixed(2)} ₺
                              </strong>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {balances.payments && balances.payments.length > 0 && (
                  <div className="settlements-section">
                    <h3>💸 {t('summary.settlements.title')}</h3>
                    <p className="settlements-description">
                      {t('summary.settlements.description')}
                    </p>
                    <div className="settlements-list">
                      {balances.payments.map((payment, index) => (
                        <div key={index} className="settlement-card">
                          <div className="settlement-from">
                            <span className="settlement-label">{t('summary.settlements.debtor')}:</span>
                            <strong>{payment.from_user_name}</strong>
                          </div>
                          <div className="settlement-arrow">→</div>
                          <div className="settlement-to">
                            <span className="settlement-label">{t('summary.settlements.creditor')}:</span>
                            <strong>{payment.to_user_name}</strong>
                          </div>
                          <div className="settlement-amount">
                            ₺{parseFloat(payment.amount).toFixed(2)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="summary-totals">
                  <div className="total-item">
                    <span>{t('summary.totalShared')}:</span>
                    <strong>₺{parseFloat(balances.total_shared || 0).toFixed(2)}</strong>
                  </div>
                </div>
              </>
            ) : (
              <div className="empty-state">
                <p>{t('summary.noSharedExpenses')}</p>
              </div>
            )}
            </>
            )}
          </div>
        )}

        {/* ÜYELER SEKMESİ */}
        {activeTab === 'members' && (
          <div className="members-tab">
            <h2>{t('members.title')}</h2>
            <p style={{color: '#666', marginBottom: '10px'}}>{t('members.total', { count: members.length })}</p>
            {members.length === 0 ? (
              <div className="empty-state">
                <p>{t('members.loadingOrNotFound')}</p>
                <button onClick={loadMembers} className="btn btn-primary" style={{marginTop: '10px'}}>
                  🔄 {t('members.refresh')}
                </button>
              </div>
            ) : (
            <div className="members-list">
              {members.map((member) => (
                <div key={member.id} className="member-card">
                  <div className="member-info">
                    <div className="member-avatar">
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="member-details">
                      <h3>{member.name}</h3>
                      <p>{member.email}</p>
                    </div>
                  </div>
                  <div className="member-actions">
                    {isAdmin && member.id !== user?.id ? (
                      <>
                        <select
                          value={member.role}
                          onChange={(e) => handleUpdateMemberRole(member.id, e.target.value)}
                          className="role-select"
                        >
                          <option value="member">👤 {t('dashboard.roles.member')}</option>
                          <option value="moderator">⭐ {t('dashboard.roles.moderator')}</option>
                          <option value="admin">👑 {t('dashboard.roles.admin')}</option>
                        </select>
                        <button
                          className="btn-remove"
                          onClick={() => handleRemoveMember(member.id, member.name)}
                        >
                          🚫 {t('members.remove')}
                        </button>
                      </>
                    ) : (
                      <span className={`role-badge role-${member.role}`}>
                        {member.role === 'admin' ? `👑 ${t('dashboard.roles.admin')}` :
                         member.role === 'moderator' ? `⭐ ${t('dashboard.roles.moderator')}` : `👤 ${t('dashboard.roles.member')}`}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
        )}
      </div>

      {/* HARCAMA MODAL */}
      {showExpenseModal && (
        <div className="modal-overlay" onClick={() => setShowExpenseModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <h2>{editingExpense ? t('expenses.edit') : t('expenses.addTitle')}</h2>
            <form onSubmit={handleSaveExpense}>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('expenses.amount')} (₺) *</label>
                  <input
                    type="number"
                    name="amount"
                    value={expenseFormData.amount}
                    onChange={handleExpenseFormChange}
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('expenses.date')} *</label>
                  <input
                    type="date"
                    name="date"
                    value={expenseFormData.date}
                    onChange={handleExpenseFormChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('expenses.payer')} *</label>
                {expenseFormData.is_shared ? (
                  // Ortak harcamada tüm kullanıcıları göster
                  <div className="participant-buttons payer-buttons">
                    {members.map(member => (
                      <button
                        key={member.id}
                        type="button"
                        className={`participant-btn ${
                          expenseFormData.payer_id === member.id || expenseFormData.payer_id === String(member.id) ? 'active' : ''
                        }`}
                        onClick={() => setExpenseFormData({ ...expenseFormData, payer_id: member.id })}
                      >
                        <span className="btn-icon">👤</span>
                        <span className="btn-text">{member.name}{member.id === user?.id ? ` (${t('common.me')})` : ''}</span>
                        {(expenseFormData.payer_id === member.id || expenseFormData.payer_id === String(member.id)) && (
                          <span className="checkmark">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  // Bireysel harcamada sadece kendimi göster
                  <div className="participant-buttons payer-buttons">
                    <button
                      type="button"
                      className="participant-btn active"
                    >
                      <span className="btn-icon">👤</span>
                      <span className="btn-text">{user?.name || user?.email} ({t('common.me')})</span>
                      <span className="checkmark">✓</span>
                    </button>
                  </div>
                )}
                {!expenseFormData.is_shared && (
                  <small className="personal-hint">
                    {t('expenses.personalHint')}
                  </small>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('expenses.category')} *</label>
                  <select
                    name="category"
                    value={expenseFormData.category}
                    onChange={handleExpenseFormChange}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>{t('expenses.description')}</label>
                <textarea
                  name="description"
                  value={expenseFormData.description}
                  onChange={handleExpenseFormChange}
                  rows="3"
                  placeholder={t('expenses.descriptionPlaceholder')}
                />
              </div>

              {/* Ortak harcama sadece çoklu kullanıcıda göster */}
              {isMultiUser && (
                <>
                  <div className="form-group">
                    <label>{t('expenses.expenseType')}</label>
                    <div className="expense-type-selector">
                      <button
                        type="button"
                        className={`expense-type-btn${!expenseFormData.is_shared ? ' active personal' : ''}`}
                        onClick={() => {
                          setExpenseFormData(prev => ({
                            ...prev,
                            is_shared: false,
                            participants: [],
                            payer_id: user?.id || ''
                          }));
                        }}
                      >
                        <span className="expense-type-icon">🙋</span>
                        <span className="expense-type-text">{t('expenses.personalExpense')}</span>
                      </button>
                      <button
                        type="button"
                        className={`expense-type-btn${expenseFormData.is_shared ? ' active shared' : ''}`}
                        onClick={() => {
                          setExpenseFormData(prev => ({
                            ...prev,
                            is_shared: true,
                            participants: members.map(m => m.id)
                          }));
                        }}
                      >
                        <span className="expense-type-icon">👥</span>
                        <span className="expense-type-text">{t('expenses.sharedExpense')}</span>
                      </button>
                    </div>
                    <p className="expense-type-description">
                      {expenseFormData.is_shared
                        ? t('expenses.sharedDescription')
                        : t('expenses.personalDescription')}
                    </p>
                  </div>

                  {expenseFormData.is_shared && (
                    <div className="form-group">
                      <label>{t('expenses.participantsQuestion')} *</label>
                      <div className="participant-buttons">
                        <button
                          type="button"
                          className={`participant-btn select-all-btn ${
                            expenseFormData.participants.length === members.length ? 'active' : ''
                          }`}
                          onClick={() => {
                            if (expenseFormData.participants.length === members.length) {
                              setExpenseFormData({ ...expenseFormData, participants: [] });
                            } else {
                              setExpenseFormData({ ...expenseFormData, participants: members.map(m => m.id) });
                            }
                          }}
                        >
                          <span className="btn-icon">👥</span>
                          <span className="btn-text">{t('expenses.selectAll')}</span>
                          {expenseFormData.participants.length === members.length && (
                            <span className="checkmark">✓</span>
                          )}
                        </button>
                        {members.map(member => (
                          <button
                            key={member.id}
                            type="button"
                            className={`participant-btn ${
                              expenseFormData.participants.includes(member.id) ? 'active' : ''
                            }`}
                            onClick={() => handleParticipantToggle(member.id)}
                          >
                            <span className="btn-icon">👤</span>
                            <span className="btn-text">{member.name}</span>
                            {expenseFormData.participants.includes(member.id) && (
                              <span className="checkmark">✓</span>
                            )}
                          </button>
                        ))}
                      </div>
                      {expenseFormData.participants.length > 0 && (
                        <p className="participant-info">
                          <span className="info-icon">💰</span>
                          ${t('expenses.perPerson')}: <strong>₺{(parseFloat(expenseFormData.amount) / expenseFormData.participants.length || 0).toFixed(2)}</strong>
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}

              <div className="modal-actions">
                {editingExpense && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowExpenseModal(false);
                      handleDeleteExpense(editingExpense.id);
                    }}
                    className="btn btn-delete-expense"
                  >
                    <span className="btn-icon">🗑️</span>
                    <span className="btn-text">{t('common.delete')}</span>
                  </button>
                )}
                <div className="modal-actions-right">
                  <button type="button" onClick={() => setShowExpenseModal(false)} className="btn btn-cancel">
                    <span className="btn-icon">✖️</span>
                    <span className="btn-text">{t('common.cancel')}</span>
                  </button>
                  <button type="submit" className="btn btn-save">
                    <span className="btn-icon">💾</span>
                    <span className="btn-text">{t('common.save')}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CONFIRM MODAL */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => confirmData.singleButton && setShowConfirmModal(false)}>
          <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className={`modal-header modal-header-${confirmData.type}`}>
              <h2>{confirmData.title}</h2>
            </div>

            <div className="modal-body">
              <p className="confirm-message">{confirmData.message}</p>
            </div>

            <div className="modal-actions">
              {!confirmData.singleButton && (
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  className="btn btn-cancel"
                >
                  {t('common.cancel')}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setShowConfirmModal(false);
                  if (confirmData.onConfirm) confirmData.onConfirm();
                }}
                className={`btn btn-${confirmData.type === 'danger' ? 'danger' : confirmData.type === 'error' ? 'primary' : 'warning'}`}
              >
                {confirmData.singleButton ? t('common.ok') : confirmData.type === 'danger' ? t('dashboard.confirmYesDelete') : t('members.confirmYesRemove')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="modal-content modal-success" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header modal-header-success">
              <h2>✅ {t('common.success')}</h2>
            </div>

            <div className="modal-body">
              <p className="success-message">{successMessage}</p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setShowSuccessModal(false)}
                className="btn btn-primary"
              >
                {t('common.ok')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HouseDetail;
